import { useEffect, useState } from 'react';
import { previewScheduleSetting } from '../../../Api/ScheduleSettings/scheduleSettingsApi';
import { refusalMessage } from '../../../Api/ScheduleSettings/scheduleRefusalMessage';
import { isCalendarDate } from './scheduleRule';

// Each call runs the api's sign-in lookup in SF Sync, so typing must not fire one per key.
export const PREVIEW_DEBOUNCE_MS = 400;
export const PREVIEW_UNAVAILABLE = 'Next run dates are unavailable right now.';

export type SchedulePreview =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; runDates: string[] }
  | { status: 'failed'; message: string };

// The summary formats every date it is given, so an answer that isn't a list of them never reaches it.
const runDatesOf = (data: unknown): string[] | null => {
  const runDates = (data as { run_dates?: unknown } | null)?.run_dates;
  return Array.isArray(runDates) && runDates.every((date) => typeof date === 'string' && isCalendarDate(date))
    ? runDates
    : null;
};

/** The api's run dates for a schedule, asked once edits pause; pass a null rule while it can't be previewed. */
export const useSchedulePreview = (rule: Record<string, unknown> | null, endDate: string | null): SchedulePreview => {
  const [preview, setPreview] = useState<SchedulePreview>({ status: 'idle' });
  // A rule is rebuilt on every render, so its content, not its identity, decides when to ask again.
  const ruleKey = rule && JSON.stringify(rule);

  useEffect(() => {
    if (!ruleKey) {
      setPreview({ status: 'idle' });
      return undefined;
    }
    setPreview({ status: 'loading' });
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const payload = { schedule_rule: JSON.parse(ruleKey), end_date: endDate };
      const { data, error } = await previewScheduleSetting(payload, controller.signal);
      // The form changed or closed while this was in flight.
      if (controller.signal.aborted) return;
      if (error) {
        // A summary line has no use for axios's words, so no response at all reads as unavailable too.
        setPreview({ status: 'failed', message: refusalMessage(error, PREVIEW_UNAVAILABLE, PREVIEW_UNAVAILABLE) });
        return;
      }
      const runDates = runDatesOf(data);
      setPreview(runDates ? { status: 'ready', runDates } : { status: 'failed', message: PREVIEW_UNAVAILABLE });
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [ruleKey, endDate]);

  return preview;
};
